import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Box, Button } from '@updater/ui-uds';
import { ChatCircleDots } from '@phosphor-icons/react';
import { useTracking } from 'react-tracking';
import { TrackEventInput } from '@updater/ui-tracker';

// Import the existing chat logic hook
import { useVirtualAssistantChat } from '../../../hooks/useVirtualAssistantChat';
import { ChatColors } from '../../../configs/chat-colors';

// Types
export interface IntegratedChatProps {
  /** Custom trigger button content - if not provided, uses default VBA button */
  triggerButton?: React.ReactNode;
  /** Custom trigger button component - full control over the trigger */
  customTrigger?: (props: {
    isOpen: boolean;
    onToggle: () => void;
    onOpen: () => void;
    onClose: () => void;
  }) => React.ReactNode;
  /** Whether the chat should start open */
  defaultOpen?: boolean;
  /** Callback when chat opens */
  onOpen?: () => void;
  /** Callback when chat closes */
  onClose?: () => void;
  /** Additional tracking details to include in analytics events */
  trackingDetails?: Record<string, unknown>;
  /** Custom tracking domain (defaults to current page) */
  trackingDomain?: string;
  /** Custom class name for the overlay container */
  className?: string;
  /** Whether to show the default trigger button (defaults to true) */
  showTrigger?: boolean;
  /** Position of the overlay - 'bottom' slides up from bottom, 'right' slides in from right */
  position?: 'bottom' | 'right';
  /** Custom z-index for the overlay (defaults to 999) */
  zIndex?: number;
}

// Styled Components for bottom slide-up overlay
const BottomChatOverlay = styled(Box)<{
  isVisible: boolean;
  zIndex: number;
}>`
  position: fixed;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100vh;
  z-index: ${(props) => props.zIndex};
  transform: translateY(
    ${(props) => (props.isVisible ? '0' : 'calc(100% + 30px)')}
  );
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: ${ChatColors.overlay.SHADOW_BOTTOM};

  @media (min-width: 768px) {
    width: 50%;
    max-width: 650px;
  }

  @media (min-width: 1024px) {
    width: 650px;
    min-width: 435px;
  }
`;

// Styled Components for right slide-in overlay
const RightChatOverlay = styled(Box)<{
  isVisible: boolean;
  zIndex: number;
}>`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 100vh;
  z-index: ${(props) => props.zIndex};
  transform: translateX(${(props) => (props.isVisible ? '0' : '100%')});
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: ${(props) =>
    props.isVisible ? ChatColors.overlay.SHADOW_RIGHT : 'none'};

  @media (min-width: 768px) {
    width: 50%;
    max-width: 650px;
  }

  @media (min-width: 1024px) {
    width: 650px;
    min-width: 435px;
  }
`;

const ChatContainer = styled(Box)`
  width: 100%;
  height: 100vh;
  background: ${ChatColors.overlay.CONTAINER_BG};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: ${ChatColors.overlay.CONTAINER_RADIUS};
`;

const ChatInnerContainer = styled(Box)`
  background: ${ChatColors.overlay.INNER_BG};
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: ${ChatColors.overlay.CONTAINER_RADIUS};
`;

const DefaultTriggerButton = styled(Button)`
  color: ${ChatColors.trigger.BUTTON_GRADIENT};
  color: ${ChatColors.trigger.BUTTON_COLOR};
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: ${ChatColors.trigger.BUTTON_RADIUS};
  background: ${ChatColors.trigger.BUTTON_GRADIENT};
  color: ${ChatColors.trigger.BUTTON_COLOR};
  border-radius: ${ChatColors.trigger.BUTTON_RADIUS};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${ChatColors.trigger.BUTTON_SHADOW};
  }

  transition: all 0.2s ease;
`;

export const IntegratedChat: React.FC<IntegratedChatProps> = ({
  triggerButton,
  customTrigger,
  defaultOpen = false,
  onOpen,
  onClose,
  trackingDetails = {},
  trackingDomain,
  className,
  showTrigger = true,
  position = 'bottom',
  zIndex = 999,
}) => {
  // Chat visibility state
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // Analytics setup
  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: trackingDomain || 'integrated-chat',
  });

  // Chat logic hook with integrated close handler
  const { ChatComponent, chatComponentProps } = useVirtualAssistantChat({
    onBack: () => {
      handleClose();
    },
  });

  // Handle opening the chat
  const handleOpen = () => {
    setIsOpen(true);
    onOpen?.();

    trackEvent({
      verb: 'opened',
      object: 'integrated-chat',
      details: {
        position,
        ...trackingDetails,
      },
    });
  };

  // Handle closing the chat
  const handleClose = () => {
    setIsOpen(false);
    onClose?.();

    trackEvent({
      verb: 'closed',
      object: 'integrated-chat',
      details: {
        position,
        ...trackingDetails,
      },
    });
  };

  // Handle toggling the chat
  const handleToggle = () => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  };

  // Choose overlay component based on position
  const OverlayComponent =
    position === 'bottom' ? BottomChatOverlay : RightChatOverlay;

  // Render custom trigger if provided
  if (customTrigger) {
    return (
      <>
        {customTrigger({
          isOpen,
          onToggle: handleToggle,
          onOpen: handleOpen,
          onClose: handleClose,
        })}

        <OverlayComponent
          isVisible={isOpen}
          zIndex={zIndex}
          className={className}
        >
          <ChatContainer>
            <ChatInnerContainer>
              <ChatComponent {...chatComponentProps} />
            </ChatInnerContainer>
          </ChatContainer>
        </OverlayComponent>
      </>
    );
  }

  return (
    <>
      {showTrigger && (
        <>
          {triggerButton ? (
            <Box onClick={handleToggle}>{triggerButton}</Box>
          ) : (
            <DefaultTriggerButton
              data-testid="integrated-chat-trigger"
              onClick={handleToggle}
            >
              <ChatCircleDots size={20} />
              {isOpen
                ? 'Close Virtual Building Assistant'
                : 'Chat with Virtual Building Assistant'}
            </DefaultTriggerButton>
          )}
        </>
      )}

      <OverlayComponent
        isVisible={isOpen}
        zIndex={zIndex}
        className={className}
      >
        <ChatContainer>
          <ChatInnerContainer>
            <ChatComponent {...chatComponentProps} />
          </ChatInnerContainer>
        </ChatContainer>
      </OverlayComponent>
    </>
  );
};

export default IntegratedChat;
